import { BankAccountModels } from '../models/bank-accounts';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import { ReactHTMLElement, useEffect, useState } from "react";
import { FormMode, FormOptionDto, MasterDto } from "src/models";
import useApi from "src/configs/api";
import { Button } from "src/components/ui/button";
import { DialogClose } from "@radix-ui/react-dialog";
import { useAlert } from "src/hooks/use-alert";

function classNames(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

export interface BankAccountDialogProps {
    mode : FormMode;
    data?: BankAccountModels.BankAccountDetails | null;
    onSubmit? : (updated : BankAccountModels.BankAccountDetails) => void;
    onOpenChange?: (open: boolean) => void;
}

const dialogTitle: Record<FormMode, string> = {
  add: "New Bank Account",
  edit: "Edit Bank Account",
  view: "Bank Account Details"
};

const emptyForm: Partial<BankAccountModels.BankAccountDetails> = {
  accountName: "",
  accountNumber: "",
  description: ""
};

export const BankAccountDialog = ({ mode, data, onSubmit, onOpenChange } : BankAccountDialogProps) => {
    const [formData, setFormData] = useState<Partial<BankAccountModels.BankAccountDetails>>( data ?? {} );
    const [accountTypes, setAccountTypes] = useState<MasterDto[]>([]);
    const api = useApi();
    const { showWarning, showSuccess, showError} = useAlert()

    useEffect(() => {
        fetchFormReferences();
    }, [])

    useEffect(() => {
        if(mode === "add")
            setFormData(emptyForm)
        else if(data)
            setFormData(data);
    }, [data, mode])
    
    const fetchFormReferences = async () => {
        try
        {
            const res = await api.get("/master-data/bankAccountType");
            if (res.status === 200)
            {
                 setAccountTypes(res.data.data);
            }
            else
                return null;
        }
        catch{}
    }

    const handleChanges = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target

        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const handleSubmit = async (e : React.FormEvent) => {
        e.preventDefault();
        try
        {
            if (mode === "add")
            {
                await api.post(`/finance/bank-accounts`, formData);
                showSuccess("Created");
            }
            else if (mode === "edit")
            {
                await api.put(`/finance/bank-accounts/${formData.accountId}`, formData);
                showSuccess("Updated");
            }

            onSubmit?.(formData as BankAccountModels.BankAccountDetails);
            onOpenChange?.(false);
        }
        catch {
            showError("", "An error occured while trying to proceed your request.")
        }
    }

    // if (!data) return null;
    return(
        <DialogContent showCloseButton={false}   className="sm:max-w-md text-base rounded-lg">
            <DialogHeader >
                <DialogTitle className="text-large text-center">
                    {dialogTitle[mode]}
                </DialogTitle>
            </DialogHeader>
            <form className="space-y-3" onSubmit={handleSubmit} method="POST">
                <div>
                <label htmlFor="Account Name" className="block text-sm font-semibold leading-6 text-gray-900">
                    {`Account Name`}
                </label>
                <div className="mt-2">
                    <input
                        type='text'
                        name='accountName'
                        value={formData.accountName ?? ""}
                        onChange={handleChanges}
                        disabled={mode === "view"}
                        required
                        className={ classNames(
                            "block w-full rounded-md border-0 py-2 px-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 required:border-pink-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6",
                             mode === "view" && ` disabled:bg-gray-50 `                       
                        )}
                    ></input>
                </div>
                </div>

                <div>
                    <label htmlFor="accountTypeId" className="block text-sm font-semibold leading-6 text-gray-900">
                        {`Type`}
                    </label>
                    <div className="mt-2">
                        <select
                            name='accountTypeId'
                            onChange={handleChanges}
                            value={formData.accountTypeId ?? ""}
                            required
                            disabled={mode === "view"}
                            className={classNames(
                                `block w-full rounded-md border-0 py-2 px-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 required:border-pink-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`,
                                mode === "view" && ` disabled:bg-gray-50`
                            )}
                        >
                            <option value="">Select Type</option>
                            {accountTypes.map(o => (
                                <option key={o.idEnum} value={o?.idEnum}>
                                    {o?.value}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div>
                <label htmlFor="Account Number" className="block text-sm font-semibold leading-6 text-gray-900">
                    {`Account Number`}
                </label>
                <div className="mt-2">
                    <input
                        type='text'
                        name='accountNumber'
                        value={formData.accountNumber ?? ""}
                        onChange={handleChanges}
                        required
                        disabled={mode === "view"}
                        className={classNames(
                            `block w-full rounded-md border-0 py-2 px-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 required:border-pink-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`,
                            mode === "view" && ` disabled:bg-gray-50 `
                        )}
                    ></input>
                </div>
                </div>                

                <div>
                    <label htmlFor="Notes" className="block text-sm font-semibold leading-6 text-gray-900">
                        {`Notes`}
                    </label>
                    <div className="mt-2">
                        <textarea
                            name='description'
                            onChange={handleChanges}
                            value={formData.description ?? ""}
                            disabled={mode === "view"}
                            className={classNames(
                            `block w-full rounded-md border-0 py-2 px-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 required:border-pink-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6`,
                            mode === "view" && ` disabled:bg-gray-50 `
                        )}
                            // className="block w-full rounded-md border-0 py-2 px-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 required:border-pink-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        >
                        </textarea>
                    </div>
                </div>
                
                <div className="flex flex-col pt-4 md:border-t-2 md:border-dashed md:flex-row-reverse gap-4">
                    {mode !== "view" && (
                        <Button type='submit' variant={"default"}  className="md:basis-2/3 bg-black text-white px-4 py-2 font-semibold md:py-1 rounded-lg">
                            {mode === "add" && "Save Account"}
                            {mode === "edit" && "Update Account"}
                        </Button>
                    )}
                    <DialogClose asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        className="px-3 py-1 text-red-500 border border-red-500 rounded-lg font-semibold hover:border-transparent hover:bg-red-300 hover:text-white"
                    >
                        Cancel
                    </Button>
                    </DialogClose>
                </div>
            </form>
        </DialogContent>
    )
}